import { createActor, assign, setup, fromPromise } from 'xstate';

import { setupAxios } from '../../setup-axios';

import { phase1Call } from './phase1';
import { phase2Call } from './phase2';
import { phase3Call } from './phase3';
import { phase4Call } from './phase4';
import { v2Check } from './v2check';
import { launchDarklyInit } from './launch-darkly-init';
import { launchDarklyFallback } from './launch-darkly-fallback';
import { LogService } from '../../services/log-service';
import { getDataForUsersWithAccountManagementRole } from './get-data-for-users-with-account-management-role';

setupAxios();

const getNiceErrorMessage = (error = {}) => {
  const { message, config = {} } = error;
  const { url } = config;
  if (url) {
    return `${message}. Url was ${url}`;
  }
  return message;
};

const showError = (error) => {
  LogService.logError(error);
  // we delay showing the error in case of redirects
  setTimeout(() => {
    const errorCover = document.querySelector('#error-cover');
    const errorDetailsMessage = document.querySelector('#error-details-message');
    const niceErrorMessage = getNiceErrorMessage(error);
    errorDetailsMessage.innerHTML = `${niceErrorMessage}`;

    errorCover.style.display = 'block';
  }, 1000);
};

const loadApp = () => {
  import('../../app').catch((error) => {
    showError(error);
    // oxlint-disable-next-line no-console
    console.error(error);
  });
};

const splashMachine = setup({
  actors: {
    doLaunchDarklyInit: fromPromise(launchDarklyInit),
    // phase 1 is base data like seller, user, some other stuff
    makePhase1Call: fromPromise(phase1Call),
    doLaunchDarklyFallback: fromPromise(launchDarklyFallback),
    // phase 2 is the rest of seller data that depends on phase 1
    makePhase2Call: fromPromise(phase2Call),
    // v2 check can happen once we have sellerSettings, userSettings, and currentUser
    makeV2Check: fromPromise(v2Check),
    makeGetDataForUsersWithAccountManagementRole: fromPromise(getDataForUsersWithAccountManagementRole),
    // phase 3 is reference data
    makePhase3Call: fromPromise(phase3Call),
    // phase 4 is odds and ends - pr builds, log rocket init, reads print settings
    makePhase4Call: fromPromise(phase4Call),
  },
}).createMachine({
  id: 'splash-screen-machine',
  initial: 'start',
  context: {
    data: {},
    error: undefined,
  },
  states: {
    start: {
      on: {
        NEXT: 'launchDarklyInit',
      },
    },
    launchDarklyInit: {
      invoke: {
        src: 'doLaunchDarklyInit',
        onDone: {
          target: 'phase1',
          actions: assign({
            data: ({ context, event }) => ({
              ...context.data,
              ...event.output,
            }),
          }),
        },
        onError: {
          target: 'error',
          actions: assign({
            error: ({ event }) => event.error,
          }),
        },
      },
    },
    phase1: {
      invoke: {
        src: 'makePhase1Call',
        onDone: {
          target: 'launchDarklyFallback',
          actions: assign({
            data: ({ context, event }) => ({
              ...context.data,
              ...event.output,
            }),
          }),
        },
        onError: {
          target: 'error',
          actions: assign({
            error: ({ event }) => event.error,
          }),
        },
      },
    },
    launchDarklyFallback: {
      invoke: {
        src: 'doLaunchDarklyFallback',
        input: ({ context: { data } }) => data,
        onDone: {
          target: 'phase2',
          actions: assign({
            data: ({ context, event }) => ({
              ...context.data,
              ...event.output,
            }),
          }),
        },
        onError: {
          target: 'error',
          actions: assign({
            error: ({ event }) => event.error,
          }),
        },
      },
    },
    phase2: {
      invoke: {
        src: 'makePhase2Call',
        input: ({ context: { data } }) => data,
        onDone: {
          target: 'v2Check',
          actions: assign({
            data: ({ context, event }) => ({
              ...context.data,
              ...event.output,
            }),
          }),
        },
        onError: {
          target: 'error',
          actions: assign({
            error: ({ event }) => event.error,
          }),
        },
      },
    },
    v2Check: {
      invoke: {
        src: 'makeV2Check',
        input: ({ context: { data } }) => data,
        onDone: {
          target: 'getDataForUsersWithAccountManagementRole',
        },
        onError: {
          target: 'error',
          actions: assign({
            error: ({ event }) => event.error,
          }),
        },
      },
    },
    getDataForUsersWithAccountManagementRole: {
      invoke: {
        src: 'makeGetDataForUsersWithAccountManagementRole',
        input: ({ context: { data } }) => data,
        onDone: {
          target: 'phase3',
          actions: assign({
            data: ({ context, event }) => ({
              ...context.data,
              ...event.output,
            }),
          }),
        },
        onError: {
          target: 'error',
          actions: assign({
            error: ({ event }) => event.error,
          }),
        },
      },
    },
    phase3: {
      invoke: {
        src: 'makePhase3Call',
        input: ({ context: { data } }) => data,
        onDone: {
          target: 'phase4',
          actions: assign({
            data: ({ context, event }) => ({
              ...context.data,
              ...event.output,
            }),
          }),
        },
        onError: {
          target: 'error',
          actions: assign({
            error: ({ event }) => event.error,
          }),
        },
      },
    },
    phase4: {
      invoke: {
        src: 'makePhase4Call',
        input: ({ context: { data } }) => data,
        onDone: {
          target: 'loadApp',
          actions: assign({
            data: ({ context, event }) => ({
              ...context.data,
              ...event.output,
            }),
          }),
        },
        onError: {
          target: 'error',
          actions: assign({
            error: ({ event }) => event.error,
          }),
        },
      },
    },
    loadApp: {
      entry: ({ context }) => {
        window.getInitData = () => context.data;
        loadApp();
      },
      type: 'final',
    },
    error: {
      entry: ({ context }) => {
        const { error } = context;
        showError(error);
      },
      type: 'final',
    },
  },
});
const splashActor = createActor(splashMachine);

export const init = () => {
  splashActor.start();
  splashActor.send({ type: 'NEXT' });
};
